import { useEffect, useRef, useState } from 'react'
import {
    FormFieldsModel,
    ModelAnswerFromProps,
} from '../models/StoreDetailModel'
import { updateFormAnswerSchedule } from '../../../../db-sqlite/repositories/schedule.repository'

// Convierte un field_text guardado ("1,5", "1.5", "", undefined) a número o null si no es válido.
export const parsePonderateValue = (value?: string): number | null => {
    if (value === undefined || value === null) return null
    const trimmed = value.toString().trim()
    if (trimmed === '') return null
    const parsed = parseFloat(trimmed.replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : null
}

interface UsePonderateGroupParams {
    fields: FormFieldsModel[]
    answers?: ModelAnswerFromProps[]
    answerProp?: ModelAnswerFromProps
    form_id: string
    schedule_id: string
    store_id: string
    checkComplete: (option?: ModelAnswerFromProps) => void
}

/**
 * Agrupa fields con `ponderate_config` (numerator/denominator/result), calcula
 * el resultado (suma numeradores / suma denominadores, redondeado a 2 decimales)
 * y lo persiste automáticamente en el/los field(s) "result". Compartido entre
 * FieldsView (layout vertical) y PonderateNumberField (layout horizontal) para
 * no duplicar la lógica de cálculo/persistencia.
 */
export const usePonderateGroup = ({
    fields,
    answers,
    answerProp,
    form_id,
    schedule_id,
    store_id,
    checkComplete,
}: UsePonderateGroupParams) => {
    // Valores editados en esta sesión, todavía no reflejados en `answers` (prop).
    // Tienen prioridad sobre lo persistido.
    const [localValues, setLocalValues] = useState<Record<string, string>>({})
    // Último valor de "result" persistido por field, para no reescribir la misma
    // respuesta en cada render.
    const lastPersistedResultRef = useRef<Record<string, string>>({})

    const getFieldSavedText = (field: FormFieldsModel): string | undefined => {
        if (field._id && field._id in localValues) {
            return localValues[field._id]
        }
        const saved = (answers ?? []).find(
            (a) => a && field._id && a._id === field._id,
        )
        return saved?.field_text
    }

    const onFieldComplete =
        (field: FormFieldsModel) => (option?: ModelAnswerFromProps) => {
            if (
                option &&
                field._id &&
                (field.ponderate_config?.role === 'numerator' ||
                    field.ponderate_config?.role === 'denominator')
            ) {
                setLocalValues((prev) => ({
                    ...prev,
                    [field._id]: option.field_text ?? '',
                }))
            }
            checkComplete(option)
        }

    const numeratorFields = fields.filter(
        (f) => f.ponderate_config?.role === 'numerator',
    )
    const denominatorFields = fields.filter(
        (f) => f.ponderate_config?.role === 'denominator',
    )
    const resultFields = fields.filter(
        (f) => f.ponderate_config?.role === 'result',
    )

    // El resultado sólo se calcula si hay al menos un numerador y un denominador
    // configurados, y todos tienen un valor numérico válido cargado. Si falta algo
    // (fields todavía sin responder), `resultText` queda en `null` y no se pisa
    // ninguna respuesta previa. Si el denominador da 0, no se puede dividir: se
    // limpia el result (string vacío) en vez de dejar un valor calculado stale.
    let resultText: string | null = null
    if (
        resultFields.length > 0 &&
        numeratorFields.length > 0 &&
        denominatorFields.length > 0
    ) {
        const numeratorValues = numeratorFields.map((f) =>
            parsePonderateValue(getFieldSavedText(f)),
        )
        const denominatorValues = denominatorFields.map((f) =>
            parsePonderateValue(getFieldSavedText(f)),
        )
        const allValid =
            numeratorValues.every((v) => v !== null) &&
            denominatorValues.every((v) => v !== null)
        if (allValid) {
            const numeratorSum = numeratorValues.reduce(
                (acc, v) => acc + (v as number),
                0,
            )
            const denominatorSum = denominatorValues.reduce(
                (acc, v) => acc + (v as number),
                0,
            )
            if (denominatorSum !== 0) {
                const res = numeratorSum / denominatorSum
                resultText = Math.round((res * 100)).toString()
            } else {
                resultText = ''
            }
        }
    }

    useEffect(() => {
        if (resultText === null || !answerProp) return
        resultFields.forEach((field) => {
            if (!field._id) return
            if (lastPersistedResultRef.current[field._id] === resultText) {
                return
            }
            lastPersistedResultRef.current[field._id] = resultText

            const resultAnswer: ModelAnswerFromProps = {
                ...answerProp,
                _id: field._id,
                field_text: resultText,
                field_media: '',
                questionPrice: resultText,
                questionInput: resultText,
                score: field?.score ? field.score.toString() : '0',
                questionScore: field?.questionScore ?? 0,
                ponderar: !!(field?.questionScore && field.questionScore > 0),
                question: field?.label || (field as any)?.title || '',
                questionType: (field as any)?.type,
            }
            updateFormAnswerSchedule({
                newAnswer: resultAnswer,
                schedule_id,
                form_id,
                store_id,
            })
            checkComplete(resultAnswer)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultText, resultFields.map((f) => f._id).join('|')])

    return {
        getFieldSavedText,
        onFieldComplete,
        numeratorFields,
        denominatorFields,
        resultFields,
        resultText,
    }
}
